import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import UnassignedTaskModal from "../../Resources/UnassignedTaskModal";
import { useResourcesService, useRoadmapService } from '../../../services';
import { notify } from 'react-notify-toast';
import { useJwt } from "../../../helpers/common";
import useIntegrationStatus from "../../../hooks/useIntegrationStatus";
import CalendarDate from '../components/Timeline/CalendarDate';
import TimelineHeader from '../components/Timeline/TimelineHeader';
import TimelineGrid from '../components/Timeline/TimelineGrid';
import TimelineLeftPanel from '../components/Timeline/TimelineLeftPanel';
import TaskAnalyticsSidebar from '../components/Timeline/TaskAnalyticsSidebar';
import SidebarAllTasksDrawer from '../components/Timeline/SidebarAllTasksDrawer';
import UnallocatedTasksDrawer from '../components/Timeline/UnallocatedTasksDrawer';
import DeleteTaskModal from '../components/Timeline/DeleteTaskModal';
import AssignTaskModal from '../components/Timeline/AssignTaskModal';
import AllTasksDrawer from '../../../components/StackedCards/AllTasksDrawer';
import LeftPanelSkeleton from '../components/LeftPanelSkeleton';
import TimelineGridSkeleton from '../components/TimelineGridSkeleton';
import { resetTimelineState } from "../../../redux/slices/timeline";
import { resetRoadmap } from "../../../redux/slices/roadmap";
import { navigateToSprintIndex, setUnallocatedDrawerOpen } from "../../../redux/slices/resources";
import TaskCompletionUpdateModal from "../../Projects/sections/TaskTableModal";
import ShowSingleProjectModal from "../ShowSingleProjectModal";
import { Sprint } from "../../../services/organization";
import { useDraggable } from "react-use-draggable-scroll";

interface Task {
  id: string;
  item_id: number;
  task_title: string;
  project_name: string;
  project_color: string;
  duration: number;
  completion?: number;
  start: string;
  end: string;
  project_id?: string;
}

interface Group {
  id: string;
  title: string;
  rightTitle?: string;
}

interface TimelineProps {
  tasks: any;
  scale: number;
  duration: number;
  groups: Group[];
  suggestedTasks: any;
  searchText?: string;
  searchBarComponent?: React.ReactNode;
  onDateRangeChange?: (startDate: string, endDate: string, resourceIds: number[]) => void;
  sprintMode?: boolean;
  currentSprint?: Sprint | null;
  sortedSprints?: Sprint[];
  currentSprintIndex?: number;
  searchType?: string;
  unallocatedTasks?: any[];
  onSprintNavigate?: (index: number) => void; // Callback for sprint filter navigation
  availableTeamsForSprint?: any[]; // Available teams for the selected sprint
  statusFilters?: string[]; // Status filters from URL (passed from parent)
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

function TimelineComponent({
  tasks,
  scale,
  duration,
  groups,
  suggestedTasks,
  searchText = "",
  searchBarComponent,
  onDateRangeChange,
  sprintMode = false,
  currentSprint = null,
  sortedSprints = [],
  currentSprintIndex = -1,
  searchType = '',
  unallocatedTasks = [],
  onSprintNavigate,
  availableTeamsForSprint = [],
  statusFilters = [],
  hasActiveFilters = false,
  onClearFilters,
}: TimelineProps) {

  const dispatch = useDispatch();
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const { removeAssignedTask, saveAssignedTask } = useResourcesService();
  const { fetchData: fetchProjectData } = useRoadmapService();
  const { isIntegrationActive } = useIntegrationStatus();

  const [taskAssignModalShow, setTaskAssignModalShow] = useState(false)
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [selectedPorject, setSelectedPorject] = useState('')
  const [selectedTask, setSelectedTask] = useState({})
  const [deleteConfirmModalShow, setDeleteConfirmModalShow] = useState(false)
  const [showViewMoreOpenSlot, setViewMoreOpenSlot] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [curSelectAssigneeGroup, setCurSelectAssigneeGroup] = useState<any>({})
  const [groupAssignedTask, setCurrentGroupAssignedTask] = useState<any>({})
  const [currentDate, setCurrentDate] = useState<moment.Moment | null>(null)
  const [additionalTask, setAdditionalTask] = useState<any[]>([])
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [containerWidth, setContainerWidth] = useState(0)
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false)
  const [isUnallocatedTask, setIsUnallocatedTask] = useState(false)
  const [isAllTasksDrawerOpen, setIsAllTasksDrawerOpen] = useState(false);
  const [drawerTasks, setDrawerTasks] = useState<any[]>([]);
  const [drawerStatusFilters, setDrawerStatusFilters] = useState<string[]>([]);
  const [drawerTypeFilter, setDrawerTypeFilter] = useState<string>('');
  const [drawerFilterTitle, setDrawerFilterTitle] = useState<string>('');
  const [selectedResourceForDrawer, setSelectedResourceForDrawer] = useState<string | null>(null);
  const [isSidebarAllTasksDrawerOpen, setIsSidebarAllTasksDrawerOpen] = useState(false);
  const [sidebarDrawerInitialTab, setSidebarDrawerInitialTab] = useState<'all' | 'allocated' | 'unallocated' | undefined>(undefined);
  const timelineContainerRef = React.useRef<HTMLDivElement | null>(null);
  const unallocatedPanelRef = React.useRef<HTMLDivElement>(null);
  const [unallocatedPanelHeight, setUnallocatedPanelHeight] = useState(0);
  const [expandedSidebarWidth, setExpandedSidebarWidth] = useState(384); // Dynamic width from TaskAnalyticsSidebar
  const ref = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(ref);

  const {
    unassignedTasksLoading,
    manageResourcesLoading,
    capacityPlanningLoading,
    capacityPlanningData,
    teamFilterState,
    teamSprints,
    capacityPlanningFilters,
    isUnallocatedDrawerOpen,
    utilizationFilters
  } = useSelector((state: any) => state.resources);

  const selectedTeams = capacityPlanningFilters?.selectedTeams || [];

  const { teamsLoading } = useSelector((state: any) => state.organization);
  const { members: projectMembers, data: projectTasks } = useSelector((state: any) => state.roadmap);

  const {
    org_holidays = [],
  } = capacityPlanningData

  const azureActive = isIntegrationActive('azure');

  // Determine if right sidebar should be shown
  // Show only when Azure is active AND in sprint filter mode AND teams are available for the sprint
  // Chain: Azure active → Sprint filter → Teams imported → Stats drawer/unallocated tasks
  const shouldShowRightSidebar = azureActive && searchType === 'sprint' && sprintMode && availableTeamsForSprint.length > 0;

  // Update height when panel visibility or state changes
  useEffect(() => {
    // Hide panel when not in sprint mode or no teams selected
    if (!shouldShowRightSidebar || searchType !== 'sprint' || !selectedTeams || selectedTeams.length === 0) {
      setUnallocatedPanelHeight(0);
      return;
    }
    // Panel is visible - height is managed by UnallocatedTasksDrawer's onHeightChange callback
  }, [shouldShowRightSidebar, searchType, selectedTeams]);

  // Auto-open/close drawer based on unallocated tasks
  useEffect(() => {
    // Only auto-manage drawer in sprint filter mode
    if (searchType !== 'sprint' || !shouldShowRightSidebar) {
      return;
    }

    // Don't auto-manage when loading
    if (capacityPlanningLoading) {
      return;
    }

    const hasUnallocatedTasks = unallocatedTasks && unallocatedTasks.length > 0;
    const shouldBeOpen = hasUnallocatedTasks;

    // Only update if state is different to avoid unnecessary dispatches
    if (isUnallocatedDrawerOpen !== shouldBeOpen) {
      dispatch(setUnallocatedDrawerOpen(shouldBeOpen));
    }
  }, [unallocatedTasks, searchType, shouldShowRightSidebar, capacityPlanningLoading, isUnallocatedDrawerOpen, dispatch]);

  // Determine if exactly one team is selected in sprint mode
  // This is used to enable stack_rank-based sorting
  const isSingleTeamSelected = searchType === 'sprint' && selectedTeams.length === 1;

  // Handle unallocated task clicks - convert to same format as allocated tasks
  const handleUnallocatedTaskClick = (task: any) => {
    // Convert unallocated task to the format expected by handleElementClick
    const formattedTask = {
      id: task.item_id,
      item_id: task.item_id,
      task_title: task.task_title,
      project_name: task.project_name,
      project_color: task.project_color,
      duration: task.duration,
      completion: task.completion,
      start: task.start,
      end: task.end,
      project_id: task.project_id,
      status: task.status,
      priority: task.priority,
      story_points: task.story_points,
      integration_item_type: task.integration_item_type,
      due_date: task.due_date,
      skill: task.skill
    };

    handleElementClick(null, formattedTask, true);
  };

  const handleViewResourceTasks = (resourceId: string) => {
    const resourceTasksMap = tasks[resourceId] || {};
    const allTasks = Object.values(resourceTasksMap).flat();
    setDrawerTasks(allTasks);
    setDrawerStatusFilters([]); // Reset status filters
    setDrawerTypeFilter(''); // Reset type filter
    setSelectedResourceForDrawer(resourceId);
    setIsAllTasksDrawerOpen(true);
  };

  // Handler for opening all tasks drawer from sidebar Total Tasks click
  const handleSidebarTotalTasksClick = () => {
    setIsSidebarAllTasksDrawerOpen(true);
  };

  // Get flattened allocated tasks for the sidebar drawer
  const getAllocatedTasksForSidebar = useMemo(() => {
    if (!tasks) return [];
    return Object.values(tasks).flatMap((dateMap: any) =>
      dateMap ? Object.values(dateMap).flatMap((list: any) => Array.isArray(list) ? list : []) : []
    );
  }, [tasks]);

  // Get resource name for drawer title
  const getResourceNameForDrawer = () => {
    if (!selectedResourceForDrawer) return undefined;
    const resource = sortedGroups.find((g: any) => String(g.id) === String(selectedResourceForDrawer));
    return resource?.title || undefined;
  };

  // Calculate the right sidebar width for timeline calculations
  const rightSidebarWidth = shouldShowRightSidebar ? (isRightSidebarCollapsed ? 64 : expandedSidebarWidth) : 0;

  // Sort groups by title (resource name) alphabetically
  const sortedGroups = useMemo(() => {
    if (!groups || !Array.isArray(groups)) return [];
    return [...groups].sort((a, b) => {
      const nameA = (a.title || '').toLowerCase();
      const nameB = (b.title || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [groups]);

  // Helper function to refresh capacity planning
  // Dispatches an event that ResourcesUtilizations listens to
  // The event handler will update the payload to trigger API call
  const refreshCapacityPlanning = () => {
    // Dispatch event to trigger refresh - ResourcesUtilizations will handle it
    window.dispatchEvent(new CustomEvent('capacity-planning:refresh'));
  }

  useEffect(() => {
    // Set current date based on sprint mode
    if (sprintMode && currentSprint && currentSprint.start_date) {
      // In sprint mode, set to sprint start date
      setCurrentDate(moment(currentSprint.start_date));
    } else {
      // Use URL-synced date range if available, otherwise default to 2 weeks before today
      if (utilizationFilters?.currentDateRange?.startDate) {
        setCurrentDate(moment(utilizationFilters.currentDateRange.startDate));
      } else {
        setCurrentDate(moment().subtract(2, 'weeks'));
      }
    }
  }, [sprintMode, currentSprint, utilizationFilters?.currentDateRange?.startDate])

  // Measure container width aggressively
  // IMPORTANT: Measure the parent container width, not the timeline container width,
  // because the timeline container has marginRight that changes with drawer state.
  // Since the drawer is a fixed overlay, it shouldn't affect grid width calculations.
  const measureContainerWidth = React.useCallback(() => {
    if (timelineContainerRef.current) {
      // Get the parent container (the flex container that holds the timeline)
      const parentElement = timelineContainerRef.current.parentElement;
      if (parentElement) {
        // Measure parent width which doesn't have the marginRight
        const width = parentElement.offsetWidth;
        if (width > 0) {
          setContainerWidth(width);
        }
      } else {
        // Fallback to measuring the timeline container itself
        const width = timelineContainerRef.current.offsetWidth;
        if (width > 0) {
          // Add back the marginRight since drawer is an overlay
          const computedStyle = window.getComputedStyle(timelineContainerRef.current);
          const marginRight = parseFloat(computedStyle.marginRight) || 0;
          setContainerWidth(width + marginRight);
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      measureContainerWidth();
    };
    window.addEventListener('resize', handleResize);

    // Initial container width measurement with timeout to ensure DOM is ready
    const timer = setTimeout(() => {
      measureContainerWidth();
    }, 0);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [measureContainerWidth])

  // Update container width when timeline container ref changes or sprint mode changes
  useEffect(() => {
    measureContainerWidth();
  }, [timelineContainerRef.current, sprintMode, measureContainerWidth]);

  // Use ResizeObserver for more reliable width tracking
  useEffect(() => {
    if (timelineContainerRef.current) {
      const observer = new ResizeObserver(() => {
        measureContainerWidth();
      });
      observer.observe(timelineContainerRef.current);
      return () => observer.disconnect();
    }
  }, [measureContainerWidth]);

  // Calculate sprint working days count
  // When teamSprints has multiple sprints, calculate from earliest start to latest end
  const sprintWorkingDaysCount = useMemo(() => {
    if (!sprintMode) return 0;

    // If we have teamSprints with multiple sprints, use them to calculate date range
    if (searchType === 'sprint' && teamSprints && teamSprints.length > 0) {
      // Calculate from earliest sprint start to latest sprint end
      const dates = teamSprints
        .map((s: any) => [s.start_date, s.end_date])
        .flat()
        .filter(Boolean)
        .map((d: string) => moment(d));

      if (dates.length === 0) return 0;

      const startDate = moment.min(dates);
      const endDate = moment.max(dates);

      let count = 0;
      let date = startDate.clone();

      // Get org working days from localStorage
      let orgWorkingDays: number[] = [];
      if (org_id && localStorage) {
        let workingDay = localStorage.getItem("workingDays") || "";
        let workingDays = workingDay ? JSON.parse(workingDay) : {};
        orgWorkingDays = workingDays[org_id] || [];
      }

      const orgHolidays = (org_holidays as any[]).map((d: any) => d.date);

      while (date.isSameOrBefore(endDate)) {
        const formattedDate1 = date.format("YYYY-MM-DDT00:00:00");
        const isHoliday = orgHolidays.includes(formattedDate1);

        // Convert day (0=Sunday, 6=Saturday) to (1=Monday, 7=Sunday)
        const d = date.day() === 0 ? 7 : date.day();

        // Check if it's a working day and not a holiday
        const isWorkingDay = orgWorkingDays.length
          ? orgWorkingDays.includes(d)
          : (d !== 0 && d !== 6 && d !== 7); // Default Mon-Fri

        if (isWorkingDay && !isHoliday) {
          count++;
        }

        date.add(1, 'days');
      }

      return count;
    }

    // Fallback to currentSprint if teamSprints not available
    if (!currentSprint || !currentSprint.start_date || !currentSprint.end_date) return 0;

    const startDate = moment(currentSprint.start_date);
    const endDate = moment(currentSprint.end_date);
    let count = 0;
    let date = startDate.clone();

    // Get org working days from localStorage
    let orgWorkingDays: number[] = [];
    if (org_id && localStorage) {
      let workingDay = localStorage.getItem("workingDays") || "";
      let workingDays = workingDay ? JSON.parse(workingDay) : {};
      orgWorkingDays = workingDays[org_id] || [];
    }

    const orgHolidays = (org_holidays as any[]).map((d: any) => d.date);

    while (date.isSameOrBefore(endDate)) {
      const formattedDate1 = date.format("YYYY-MM-DDT00:00:00");
      const isHoliday = orgHolidays.includes(formattedDate1);

      // Convert day (0=Sunday, 6=Saturday) to (1=Monday, 7=Sunday)
      const d = date.day() === 0 ? 7 : date.day();

      // Check if it's a working day and not a holiday
      const isWorkingDay = orgWorkingDays.length
        ? orgWorkingDays.includes(d)
        : (d !== 0 && d !== 6 && d !== 7); // Default Mon-Fri

      if (isWorkingDay && !isHoliday) {
        count++;
      }

      date.add(1, 'days');
    }

    return count;
  }, [sprintMode, currentSprint, teamSprints, searchType, org_id, org_holidays]);

  const leftboxWidth = 280 * scale; // Increased width to accommodate story points

  // Calculate box width based on sprint mode
  const boxWidth = useMemo(() => {
    if (sprintMode && currentSprint && sprintWorkingDaysCount > 0) {
      // Use containerWidth if available and valid, otherwise use screenWidth
      // containerWidth is now measured from parent, so it already excludes marginRight
      // Give screenWidth higher priority for initial render
      const effectiveWidth = containerWidth > leftboxWidth ? containerWidth : screenWidth;

      // In sprint mode, always reserve 64px for the stats drawer (collapsed state)
      // The drawer can expand to 384px, but the 64px is always there as a minimum
      const statsDrawerReservedWidth = shouldShowRightSidebar ? 62 : 0;
      const availableWidth = effectiveWidth - leftboxWidth - statsDrawerReservedWidth;

      const calculatedWidth = Math.floor(availableWidth / sprintWorkingDaysCount);

      // Ensure minimum width of 80px for readability
      return Math.max(80, calculatedWidth);
    }
    // Default calculation
    return Math.max(75 * scale, Math.floor((screenWidth - 300) / 20));
  }, [sprintMode, currentSprint, sprintWorkingDaysCount, containerWidth, screenWidth, scale, leftboxWidth, shouldShowRightSidebar]);

  const boxheight = 60 * scale;

  const getRenderingDateArr = (a: string[]) => {
    // Simply return the working days array - tasks are already positioned by their start date
    // and will span across cells based on their duration
    return a || [];
  }

  const getWeekdaysArray = (startDate: moment.Moment, daysToCount: number) => {
    let datesArray: string[] = [];
    let holidayNotifyList: string[] = [];
    let holidayListObj: Record<string, string> = {};
    let count = 0;
    let date = moment(startDate); // work with a moment instance directly
    let orgWorkingDays: number[] = []
    const orgHolidays = (org_holidays as any[]).map((d: any) => d.date)

    if (org_id && localStorage) {
      let workingDay = localStorage.getItem("workingDays") ? localStorage.getItem("workingDays") : ""
      let workingDays = workingDay ? JSON.parse(workingDay) : {};
      orgWorkingDays = workingDays[org_id] ? workingDays[org_id] : [];
    }

    while (count < daysToCount) {
      const formattedDate = moment(date).format("DD MMM YYYY");
      const formattedDate1 = moment(date).format("YYYY-MM-DDT00:00:00");
      const isHoliday = orgHolidays.includes(formattedDate1);

      // Convert day (0=Sunday, 6=Saturday) to (1=Monday, 7=Sunday)
      const d = date.day() === 0 ? 7 : date.day();

      // Check if it's a working day and not a holiday
      const isWorkingDay = orgWorkingDays.length
        ? orgWorkingDays.includes(d)
        : (d !== 0 && d !== 6 && d !== 7); // Default Mon-Fri if no orgWorkingDays set

      if (isWorkingDay && !isHoliday) {
        datesArray.push(formattedDate);
        count++;
      }

      if (isHoliday) {
        const result = org_holidays.find((item: any) => item.date === formattedDate1);
        const prevDate = moment(date).format("DD MMM YYYY")
        // Guard for missing result
        if (result && result.name) {
          holidayListObj[prevDate] = result.name
        }
        holidayNotifyList.push(prevDate)
      }
      date.add(1, "days");
    }
    return { weekdaysArray: datesArray, holidayNotifyList, holidayListObj };
  }

  const checkHoliday = (date1: string, date2: string, holidayNotifyList: string[]) => {
    const format = 'DD MMM YYYY';
    const startDate = moment(date1, format);
    const endDate = moment(date2, format);

    // Check each date in arr2
    const matchingDates = holidayNotifyList.filter((date: string) => {
      const current = moment(date, format);
      return current.isBetween(startDate, endDate, 'day', '[]'); // Include boundaries
    });
    if (matchingDates.length) {
      return matchingDates;
    }
    return matchingDates
  }

  const handleDateClick = (
    e: React.MouseEvent,
    clickedDate: string,
    group: any,
    tasksData: any,
    con: boolean
  ) => {
    if (!con) {
      setSelectedDate(clickedDate)
      setCurSelectAssigneeGroup(group)
      setTaskAssignModalShow(true)
      setCurrentGroupAssignedTask(tasksData)
    }
  }

  const handleDeleteClick = (
    e: React.MouseEvent | null,
    tasksData: Task,
    group: any
  ) => {
    setDeleteConfirmModalShow(true)
    setCurrentGroupAssignedTask(tasksData)
    setCurSelectAssigneeGroup(group)
  }

  const handleAssignSuggestedTaskClick = async (tasksData: any, group: any) => {
    await saveAssignedTask({
      resource_id: group.id,
      ...tasksData
    }, true)
    // Refresh capacity planning after task assignment
    refreshCapacityPlanning();
  }

  function isColliding(rect1: DOMRect, rect2: DOMRect) {
    return !(rect1.right < rect2.left ||
      rect1.left > rect2.right ||
      rect1.bottom < rect2.top ||
      rect1.top > rect2.bottom);
  }

  const handleDeleteConfirm = async () => {
    if (!unassignedTasksLoading) {
      await removeAssignedTask({
        resource_id: curSelectAssigneeGroup.id,
        ...groupAssignedTask
      })
      refreshCapacityPlanning();
      setDeleteConfirmModalShow(false)
      // Refresh capacity planning after task removal
    }
  }
  const handleDeleteCancel = () => {
    setDeleteConfirmModalShow(false)
    setCurrentGroupAssignedTask({})
    setCurSelectAssigneeGroup({})
  }

  const handleNextClick = () => {
    if (sprintMode && sortedSprints && sortedSprints.length > 0 && currentSprintIndex >= 0) {
      // SPRINT MODE: Navigate to next sprint using index (like linked list)
      const nextIndex = currentSprintIndex + 1;
      if (nextIndex < sortedSprints.length) {
        const nextSprint = sortedSprints[nextIndex];

        // Skip if sprint has null dates
        if (!nextSprint.start_date || !nextSprint.end_date) {
          return;
        }

        // Update Redux state with new index (for team filter mode)
        if (searchType === 'team') {
          dispatch(navigateToSprintIndex(nextIndex));
        }
        // Call parent callback for sprint filter mode
        else if (searchType === 'sprint' && onSprintNavigate) {
          onSprintNavigate(nextIndex);
        }

        // Update local date state
        const _date = moment(nextSprint.start_date);
        setCurrentDate(_date);

        // Call API with sprint date range
        if (onDateRangeChange && sortedGroups) {
          const startDate = nextSprint.start_date;
          const endDate = nextSprint.end_date;
          const resourceIds = sortedGroups.map((group: any) => parseInt(group.id));
          onDateRangeChange(startDate, endDate, resourceIds);
        }
      }
    } else {
      // NORMAL MODE: Move forward by 2 weeks, show 4 weeks window
      const _date = currentDate ? moment(currentDate).add(2, 'weeks') : moment();
      setCurrentDate(_date);

      // Call API with new date range - current date is the start, +4 weeks
      if (onDateRangeChange && sortedGroups) {
        const startDate = _date.format('YYYY-MM-DD');
        const endDate = _date.clone().add(4, 'weeks').format('YYYY-MM-DD');
        const resourceIds = sortedGroups.map((group: any) => parseInt(group.id));
        onDateRangeChange(startDate, endDate, resourceIds);
      }
    }
  }

  const handlePrevClick = () => {
    if (sprintMode && sortedSprints && sortedSprints.length > 0 && currentSprintIndex >= 0) {
      // SPRINT MODE: Navigate to previous sprint using index (like linked list)
      const prevIndex = currentSprintIndex - 1;
      if (prevIndex >= 0) {
        const prevSprint = sortedSprints[prevIndex];

        // Skip if sprint has null dates
        if (!prevSprint.start_date || !prevSprint.end_date) {
          return;
        }

        // Update Redux state with new index (for team filter mode)
        if (searchType === 'team') {
          dispatch(navigateToSprintIndex(prevIndex));
        }
        // Call parent callback for sprint filter mode
        else if (searchType === 'sprint' && onSprintNavigate) {
          onSprintNavigate(prevIndex);
        }

        // Update local date state
        const _date = moment(prevSprint.start_date);
        setCurrentDate(_date);

        // Call API with sprint date range
        if (onDateRangeChange && sortedGroups) {
          const startDate = prevSprint.start_date;
          const endDate = prevSprint.end_date;
          const resourceIds = sortedGroups.map((group: any) => parseInt(group.id));
          onDateRangeChange(startDate, endDate, resourceIds);
        }
      }
    } else {
      // NORMAL MODE: Move backward by 2 weeks, show 4 weeks window
      const _date = currentDate ? moment(currentDate).subtract(2, 'weeks') : moment().subtract(2, 'weeks');
      setCurrentDate(_date);

      // Call API with new date range - current date is the start, +4 weeks
      if (onDateRangeChange && sortedGroups) {
        const startDate = _date.format('YYYY-MM-DD');
        const endDate = _date.clone().add(4, 'weeks').format('YYYY-MM-DD');
        const resourceIds = sortedGroups.map((group: any) => parseInt(group.id));
        onDateRangeChange(startDate, endDate, resourceIds);
      }
    }
  }

  const handleTodayClick = () => {
    if (sprintMode && sortedSprints && sortedSprints.length > 0) {
      // SPRINT MODE: Navigate to current/active sprint using index
      const now = moment();

      // Find the index of current sprint
      const currentIndex = sortedSprints.findIndex((sprint: Sprint) => {
        if (!sprint.start_date || !sprint.end_date) return false;
        const start = moment(sprint.start_date);
        const end = moment(sprint.end_date);
        return sprint.status === 'current' || (now.isSameOrAfter(start) && now.isSameOrBefore(end));
      });

      // Use current index if found, otherwise find next upcoming
      let targetIndex = currentIndex;
      if (targetIndex === -1) {
        targetIndex = sortedSprints.findIndex((sprint: Sprint) => {
          if (!sprint.start_date) return false;
          return moment(sprint.start_date).isAfter(now);
        });
      }

      // Fall back to first sprint if nothing found
      if (targetIndex === -1) {
        targetIndex = 0;
      }

      if (targetIndex >= 0 && targetIndex < sortedSprints.length) {
        const targetSprint = sortedSprints[targetIndex];

        // Skip if sprint has null dates
        if (!targetSprint.start_date || !targetSprint.end_date) {
          return;
        }

        // Update Redux state with new index (for team filter mode)
        if (searchType === 'team') {
          dispatch(navigateToSprintIndex(targetIndex));
        }
        // Call parent callback for sprint filter mode
        else if (searchType === 'sprint' && onSprintNavigate) {
          onSprintNavigate(targetIndex);
        }

        // Update local date state
        const _date = moment(targetSprint.start_date);
        setCurrentDate(_date);

        // Call API with sprint date range
        if (onDateRangeChange && sortedGroups) {
          const startDate = targetSprint.start_date;
          const endDate = targetSprint.end_date;
          const resourceIds = sortedGroups.map((group: any) => parseInt(group.id));
          onDateRangeChange(startDate, endDate, resourceIds);
        }
      }
    } else {
      // NORMAL MODE: Show current date with 2 weeks before and 2 weeks after (4 weeks total)
      const _date = moment().subtract(2, 'weeks');
      setCurrentDate(_date);

      // Call API with new date range - 2 weeks before today, 4 weeks total window
      if (onDateRangeChange && sortedGroups) {
        const startDate = _date.format('YYYY-MM-DD');
        const endDate = _date.clone().add(4, 'weeks').format('YYYY-MM-DD');
        const resourceIds = sortedGroups.map((group: any) => parseInt(group.id));
        onDateRangeChange(startDate, endDate, resourceIds);
      }
    }
  }

  const handleElementClick = (e: React.MouseEvent | null, ele: Task, isUnallocated: boolean = false) => {
    if (ele.project_id) {
      setSelectedPorject(ele.project_id)
      setShowTaskModal(true)
      // setShowProjectModal(true)
      setSelectedTask(ele)
      setIsUnallocatedTask(isUnallocated)
      dispatch(resetTimelineState());
      dispatch(resetRoadmap());
      fetchProjectData(ele.project_id, false, {});
    }
  }
  const handleCloseProjectModal = () => {
    setShowProjectModal(false);
    setShowTaskModal(true);
  }

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
  }

  const handleSuggestions = (more_tasks: any[] = [], group: any) => {
    if (more_tasks.length) {
      setAdditionalTask(more_tasks)
      setViewMoreOpenSlot(true)
      setCurSelectAssigneeGroup(group)
    }
  }

  const handleTaskRadioButton = (e: any, task: any) => {
    setCurrentGroupAssignedTask(task)
  }
  const handleViewMoreOpenSlotCancel = () => {
    setAdditionalTask([])
    setCurrentGroupAssignedTask({})
    setCurSelectAssigneeGroup({})
    setViewMoreOpenSlot(false)
  }

  const handleSaveClick = async () => {
    if (!unassignedTasksLoading) {
      if (groupAssignedTask?.item_id) {
        await saveAssignedTask({
          resource_id: curSelectAssigneeGroup.id,
          ...groupAssignedTask
        }, true)
        refreshCapacityPlanning();
        handleViewMoreOpenSlotCancel()
        // Refresh capacity planning after task assignment
      } else {
        notify.show("Please choose a task to continue.", "error", 2000);
      }

    }
  }
  // didn't work , just make the timeline responsive according to screen width and height, always fill the screen with the timeline grid accoridng to specific screen width and height
  // When we have multiple sprints, use earliest sprint start date
  let _date: moment.Moment;
  if (sprintMode && searchType === 'sprint' && teamSprints && teamSprints.length > 0) {
    const dates = teamSprints
      .map((s: any) => s.start_date)
      .filter(Boolean)
      .map((d: string) => moment(d));

    if (dates.length > 0) {
      _date = moment.min(dates);
    } else {
      _date = currentDate ? moment(currentDate) : (currentSprint && currentSprint.start_date ? moment(currentSprint.start_date) : moment().subtract(2, 'weeks'));
    }
  } else {
    _date = currentDate ? moment(currentDate) : (sprintMode && currentSprint && currentSprint.start_date ? moment(currentSprint.start_date) : moment().subtract(2, 'weeks'));
  }

  // Calculate working days based on sprint mode
  let workingDaysCount: number;
  let weekdaysArray: string[];
  let holidayNotifyList: string[];
  let holidayListObj: Record<string, string>;

  if (sprintMode && sprintWorkingDaysCount > 0) {
    // In sprint mode: use sprint working days count (calculated from teamSprints or currentSprint)
    workingDaysCount = sprintWorkingDaysCount;

    // Use earliest sprint start date when we have multiple sprints
    let startDateForWeekdays = _date;
    if (searchType === 'sprint' && teamSprints && teamSprints.length > 0) {
      const dates = teamSprints
        .map((s: any) => s.start_date)
        .filter(Boolean)
        .map((d: string) => moment(d));

      if (dates.length > 0) {
        startDateForWeekdays = moment.min(dates);
      }
    }

    const result = getWeekdaysArray(startDateForWeekdays, workingDaysCount);
    weekdaysArray = result.weekdaysArray;
    holidayNotifyList = result.holidayNotifyList;
    holidayListObj = result.holidayListObj;
  } else {
    // Default mode: Calculate 4 weeks of working days
    workingDaysCount = duration || 28; // Default to 28 working days (4 weeks)
    const result = getWeekdaysArray(_date, workingDaysCount);
    weekdaysArray = result.weekdaysArray;
    holidayNotifyList = result.holidayNotifyList;
    holidayListObj = result.holidayListObj;
  }

  const renderArr = getRenderingDateArr(weekdaysArray);

  let orgWorkingDays = []
  if (org_id && localStorage) {
    let workingDay: any = localStorage.getItem("workingDays") ? localStorage.getItem("workingDays") : ""
    let workingDays: any = workingDay ? JSON.parse(workingDay) : {};
    orgWorkingDays = workingDays[org_id] ? workingDays[org_id] : [];
  }

  // Check if there are multiple tasks (they're already grouped by date in the new data structure)
  const areTasksOnSameDate = (tasks: Task[]) => {
    // Tasks are already grouped by date from API, so just check if there are multiple
    return tasks && tasks.length > 1;
  };

  const manageHolidayFlags = () => {
    const holidays = document.getElementsByClassName("holiday-flag");
    const leftPanel = document.getElementById("left-panel");
    Array.from(holidays).forEach(h => {
      if (leftPanel && h) {
        if (isColliding(leftPanel.getBoundingClientRect(), h.getBoundingClientRect())) {
          h.setAttribute("style", "visibility: hidden");
        } else {
          h.setAttribute("style", "visibility: visible");
        }
      }
    })
  }

  useEffect(() => {
    const groupEl = document.getElementById("group-0")
    if (groupEl) {
      const els = groupEl.getElementsByClassName("SlackIndex absolute")
      const holidays = document.getElementsByClassName("holiday-flag")
      console.log(els, holidays)
      Array.from(els).forEach((el) => {
        el.addEventListener("mouseover", () => {
          Array.from(holidays).forEach(h => {
            const bool = isColliding(el.getBoundingClientRect(), h.getBoundingClientRect())
            console.log("bool", bool)
            if (bool) {
              h.setAttribute("style", "visibility: hidden");
            }
          })
        })
        el.addEventListener("mouseout", () => {
          Array.from(holidays).forEach(h => {
            h.setAttribute("style", "visibility: visible");
          })
        })
      })
    }
    manageHolidayFlags()
  }, [currentDate]);

  // Close AllTasksDrawer when task is updated
  useEffect(() => {
    const handleTaskUpdate = () => {
      setIsAllTasksDrawerOpen(false);
    };

    window.addEventListener('capacity-planning:refresh', handleTaskUpdate);
    return () => {
      window.removeEventListener('capacity-planning:refresh', handleTaskUpdate);
    };
  }, []);

  // Handle deep link status filter opening the drawer via URL
  useEffect(() => {
    // Wait for tasks to be available
    if (!tasks || Object.keys(tasks).length === 0) return;

    // We only open automatically if there is exactly 1 status filter set
    // And the sidebar isn't explicitly closed by the user yet
    // To support the Slack deep link behavior seamlessly
    if (statusFilters && statusFilters.length > 0) {
      if (!isSidebarAllTasksDrawerOpen && statusFilters.length === 1) {
        setSidebarDrawerInitialTab('allocated');
        setDrawerStatusFilters(Array.from(statusFilters));
        setDrawerTypeFilter('');

        // Use timeout to ensure state settles before showing drawer
        setTimeout(() => {
          setIsSidebarAllTasksDrawerOpen(true);
        }, 100);
      }
    }
  }, [tasks, statusFilters, isSidebarAllTasksDrawerOpen]);




  // Force remeasure when groups or sprint mode changes
  useEffect(() => {
    const timer = setTimeout(() => {
      measureContainerWidth();
    }, 100);
    return () => clearTimeout(timer);
  }, [sortedGroups, sprintMode, currentSprint, measureContainerWidth]);

  // Calculate if prev/next buttons should be disabled
  const isPrevDisabled = sprintMode && sortedSprints && sortedSprints.length > 0 && currentSprintIndex >= 0
    ? currentSprintIndex === 0
    : false;

  const isNextDisabled = sprintMode && sortedSprints && sortedSprints.length > 0 && currentSprintIndex >= 0
    ? currentSprintIndex === sortedSprints.length - 1
    : false;

  // Determine if we should show the skeleton
  // Show skeleton if:
  // 1. Resources are loading
  // 2. Teams are loading
  // 3. Capacity planning data is loading AND we have no groups to show
  // 4. Capacity planning data hasn't been loaded yet (allocated_task is missing) - prevents "No Team Selected" flash on first load
  // Exception: In sprint mode, if no teams are available for the sprint (teams not imported),
  // don't show skeleton — show the "Sprint Teams Not Imported" empty state instead.
  const isSprintWithNoTeams = searchType === 'sprint' && availableTeamsForSprint.length === 0 && !teamsLoading && !manageResourcesLoading;
  const shouldShowSkeleton = !isSprintWithNoTeams && (
    manageResourcesLoading ||
    teamsLoading ||
    (capacityPlanningLoading && (!sortedGroups || sortedGroups.length === 0)) ||
    (!capacityPlanningData?.allocated_task && !capacityPlanningData?.tasks)
  );

  return (
    <div className="flex w-full flex-col h-full relative">
      <TimelineHeader
        searchBarComponent={searchBarComponent}
        onPrevClick={handlePrevClick}
        onTodayClick={handleTodayClick}
        onNextClick={handleNextClick}
        searchType={searchType}
        isPrevDisabled={isPrevDisabled}
        isNextDisabled={isNextDisabled}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
      />

      {/* Unallocated Tasks Panel - Outside timeline, positioned between header and timeline */}
      {/* Show drawer only when teams are selected in sprint filter mode */}
      {/* Drawer is always shown when teams are selected (collapsed when no tasks, expanded when tasks exist) */}
      {shouldShowRightSidebar && searchType === 'sprint' && selectedTeams && selectedTeams.length > 0 && (
        <UnallocatedTasksDrawer
          unallocatedTasks={unallocatedTasks}
          isOpen={isUnallocatedDrawerOpen}
          onToggle={() => { }} // Disabled - drawer is auto-managed
          onTaskClick={handleUnallocatedTaskClick}
          isSingleTeamSelected={isSingleTeamSelected}
          isSprintFilter={searchType === 'sprint'}
          panelRef={unallocatedPanelRef}
          onHeightChange={setUnallocatedPanelHeight}
        />
      )}

      <div className="App relative flex-1">
        {shouldShowSkeleton ? (
          <div
            className="relative bg-gray-50 dark:bg-gray-900 rounded-none shadow-none w-full overflow-auto"
            style={{
              height: `calc(100vh - 75px - 65px - ${unallocatedPanelHeight}px)`,
            }}
          >
            <div className='flex w-full h-full items-start'>
              <LeftPanelSkeleton rows={15} />
              <TimelineGridSkeleton rows={15} cols={20} />
            </div>
          </div>
        ) : ""}

        {(!sortedGroups || sortedGroups.length === 0) && (
          <div
            className="relative bg-gray-50 dark:bg-gray-900 rounded-none shadow-none w-full overflow-auto flex items-center justify-center"
            style={{
              height: 'calc(100vh - 75px - 65px)',
            }}
          >
            <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-md max-w-lg">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                {searchType === 'sprint' && availableTeamsForSprint.length === 0 ? (
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                ) : (
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">
                {searchType === 'sprint' && availableTeamsForSprint.length === 0
                  ? 'Sprint Teams Not Imported'
                  : 'No Team Selected'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchType === 'sprint' && availableTeamsForSprint.length === 0
                  ? 'The teams for this sprint have not been imported into Cadence. Please import the teams or select a different sprint.'
                  : searchType === 'sprint'
                    ? 'Please select at least one team from the dropdown above to view sprint tasks.'
                    : 'No resources to display. Please adjust your filters.'}
              </p>
            </div>
          </div>
        )}

        <div className="flex h-full w-full">
          <div
            ref={(r) => {
              timelineContainerRef.current = r;
              ref.current = r as HTMLInputElement;
            }}
            {...events}
            id="box-container"
            key={`timeline-${sprintMode ? 'sprint' : 'normal'}-${currentSprint?.id || 'none'}`}
            className="relative bg-gray-50 dark:bg-gray-900 rounded-none shadow-none flex-1 overflow-auto select-none overscroll-contain !cursor-grab"
            onScroll={manageHolidayFlags}
            onDragStart={(e) => e.preventDefault()}
            style={{
              marginRight: `${rightSidebarWidth}px`,
              height: `calc(100vh - 75px - 65px - ${unallocatedPanelHeight}px)`
            }}
          >
            <CalendarDate
              duration={workingDaysCount}
              weekdaysArray={weekdaysArray}
              boxWidth={boxWidth}
              leftBoxWidth={leftboxWidth}
            />
            <div
              style={{
                width: Math.max(workingDaysCount * boxWidth, screenWidth - leftboxWidth - rightSidebarWidth - 50),
              }}
              className="relative flex h-full w-full items-start"
            >
              <TimelineLeftPanel
                groups={sortedGroups}
                leftboxWidth={leftboxWidth}
                boxheight={boxheight}
                capacityPlanningData={capacityPlanningData}
                searchType={searchType}
                teamFilterState={teamFilterState}
                selectedTeams={selectedTeams}
                onViewResourceTasks={handleViewResourceTasks}
              />
              <div id="right-pane" className="relative" style={{
                height: '100%',
                zIndex: 15
              }}>
                {capacityPlanningLoading ? (
                  <TimelineGridSkeleton rows={sortedGroups.length > 0 ? sortedGroups.length : 15} cols={20} scale={scale} showHeader={false} />
                ) : (
                  <TimelineGrid
                    groups={sortedGroups}
                    tasks={tasks}
                    suggestedTasks={suggestedTasks}
                    renderArr={renderArr || []}
                    weekdaysArray={weekdaysArray || []}
                    holidayNotifyList={holidayNotifyList || []}
                    holidayListObj={holidayListObj || {}}
                    boxWidth={boxWidth}
                    boxheight={boxheight}
                    workingDaysIn4Weeks={workingDaysCount}
                    searchText={searchText}
                    orgWorkingDays={orgWorkingDays}
                    onElementClick={handleElementClick}
                    onDeleteClick={handleDeleteClick}
                    onDateClick={handleDateClick}
                    onAssignSuggestedTaskClick={handleAssignSuggestedTaskClick}
                    onSuggestions={handleSuggestions}
                    checkHoliday={checkHoliday}
                    areTasksOnSameDate={areTasksOnSameDate}
                    isSingleTeamSelected={isSingleTeamSelected}
                    isSprintFilter={searchType === 'sprint'}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar for Unallocated Tasks */}
          {shouldShowRightSidebar && (
            <>
              <TaskAnalyticsSidebar
                tasks={tasks}
                unallocatedTasks={unallocatedTasks}
                groups={sortedGroups}
                currentSprint={currentSprint}
                sprintMode={sprintMode}
                currentDate={currentDate}
                teamFilterState={teamFilterState}
                searchType={searchType}
                isOpen={!isRightSidebarCollapsed}
                onToggle={() => setIsRightSidebarCollapsed(!isRightSidebarCollapsed)}
                stats={capacityPlanningData?.stats}
                statsPerTeam={capacityPlanningData?.stats_per_team}
                isLoading={capacityPlanningLoading}
                selectedTeams={selectedTeams}
                onFilterRequest={(type, value) => {
                  if (type === 'person') {
                    handleViewResourceTasks(value);
                  } else if (type === 'status') {
                    // Handle unallocated tasks - open AllTasksDrawer with unallocated tasks from API
                    if (value === 'unallocated') {
                      // Open AllTasksDrawer with unallocated tasks from capacityPlanningData?.unallocated_task
                      setDrawerFilterTitle('Unallocated Work Items');
                      setDrawerTasks(unallocatedTasks || []);
                      setDrawerStatusFilters([]); // No status filter needed, all are unallocated
                      setDrawerTypeFilter('');
                      setSelectedResourceForDrawer(null);
                      setIsAllTasksDrawerOpen(true);
                    } else {
                      // Flatten all allocated tasks only
                      const allAllocatedTasks = tasks ? Object.values(tasks).flatMap((dateMap: any) =>
                        dateMap ? Object.values(dateMap).flatMap((list: any) => Array.isArray(list) ? list : []) : []
                      ) : [];

                      // Format status name for title (e.g., "on_track" -> "On Track Tasks")
                      const statusLabel = value.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                      setDrawerFilterTitle(`${statusLabel} Work Items`);
                      setDrawerTasks(allAllocatedTasks);
                      setDrawerStatusFilters([value]);
                      setDrawerTypeFilter('');
                      setSelectedResourceForDrawer(null);
                      setIsAllTasksDrawerOpen(true);
                    }
                  } else if (type === 'type') {
                    // For task type filter, include both allocated and unallocated tasks
                    const allAllocatedTasks = tasks ? Object.values(tasks).flatMap((dateMap: any) =>
                      dateMap ? Object.values(dateMap).flatMap((list: any) => Array.isArray(list) ? list : []) : []
                    ) : [];
                    const allTasks = [...allAllocatedTasks, ...(unallocatedTasks || [])];

                    // Format type name for title (e.g., "user_story" -> "All User Storys")
                    const typeLabel = value.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                    setDrawerFilterTitle(`All ${typeLabel}s`);
                    setDrawerTasks(allTasks);
                    setDrawerStatusFilters([]);
                    setDrawerTypeFilter(value);
                    setSelectedResourceForDrawer(null);
                    setIsAllTasksDrawerOpen(true);
                  }
                }}
                onTotalTasksClick={handleSidebarTotalTasksClick}
                teamSprints={teamSprints}
                sprintRangeSelection={capacityPlanningFilters?.sprintRangeSelection}
                onWidthChange={setExpandedSidebarWidth}
              />
              <SidebarAllTasksDrawer
                isOpen={isSidebarAllTasksDrawerOpen}
                onClose={() => {
                  setIsSidebarAllTasksDrawerOpen(false);
                  setSidebarDrawerInitialTab(undefined); // Reset initial tab on close
                }}
                allocatedTasks={getAllocatedTasksForSidebar}
                unallocatedTasks={unallocatedTasks || []}
                onSelectTask={(task) => {
                  handleElementClick(null, task as any, unallocatedTasks?.some(t => t.item_id === task.item_id));
                }}
                onDeleteTask={(task) => {
                  handleDeleteClick(null, task as any, null);
                }}
                isSingleTeamSelected={isSingleTeamSelected}
                isSprintFilter={searchType === 'sprint'}
                initialTab={sidebarDrawerInitialTab}
              />

            </>
          )}
          <AllTasksDrawer
            tasks={drawerTasks}
            isOpen={isAllTasksDrawerOpen}
            onClose={() => {
              setIsAllTasksDrawerOpen(false);
              setDrawerStatusFilters([]); // Reset filters on close
              setDrawerTypeFilter(''); // Reset type filter on close
              setDrawerFilterTitle(''); // Reset filter title on close
            }}
            onSelectTask={(task) => {
              handleElementClick(null, task as any);
              // Do not close drawer here, let user close it manually or click multiple tasks
            }}
            onDeleteTask={(task) => {
              const group = sortedGroups.find((g: any) => g.id === selectedResourceForDrawer);
              handleDeleteClick(null, task as any, group);
            }}
            resourceName={getResourceNameForDrawer()}
            filterTitle={drawerFilterTitle}
            initialStatusFilters={drawerStatusFilters}
            initialTypeFilter={drawerTypeFilter}
            hideStatusFilter={drawerStatusFilters.length > 0 || !!drawerTypeFilter}
            isSingleTeamSelected={isSingleTeamSelected}
            isSprintFilter={searchType === 'sprint'}
          />
        </div>

        {/** show Merge Warring Modal */}
        {taskAssignModalShow && (
          <UnassignedTaskModal
            closeUnassignedTaskModal={() => setTaskAssignModalShow(false)}
            showUnassignedTaskModal={taskAssignModalShow}
            selectedDate={selectedDate}
            assigneeGroup={curSelectAssigneeGroup}
            groupAssignedTask={groupAssignedTask}
          />
        )}

        <DeleteTaskModal
          deleteConfirmModalShow={deleteConfirmModalShow}
          unassignedTasksLoading={unassignedTasksLoading}
          groupAssignedTask={groupAssignedTask}
          onDeleteConfirm={handleDeleteConfirm}
          onDeleteCancel={handleDeleteCancel}
          onCloseDeleteModal={() => setDeleteConfirmModalShow(false)}
        />

        <AssignTaskModal
          showViewMoreOpenSlot={showViewMoreOpenSlot}
          curSelectAssigneeGroup={curSelectAssigneeGroup}
          additionalTask={additionalTask}
          unassignedTasksLoading={unassignedTasksLoading}
          onTaskRadioButton={handleTaskRadioButton}
          onSaveClick={handleSaveClick}
          onCancelClick={handleViewMoreOpenSlotCancel}
          onCloseModal={() => setViewMoreOpenSlot(false)}
        />

        {showTaskModal ? (
          <TaskCompletionUpdateModal
            isVisiable={showTaskModal}
            openProject={() => {
              setShowProjectModal(true);
              handleCloseTaskModal();
            }}
            project_id={selectedPorject}
            handleAddNewTask={(flag: boolean) => { }}
            onClose={handleCloseTaskModal}
            onTaskUpdate={() => {
              window.dispatchEvent(new CustomEvent('capacity-planning:refresh'));
              handleCloseTaskModal();
            }}
            tasks={projectTasks || []}
            selectedTask={selectedTask ?? {}}
            members={projectMembers || []}
            sim_flag={false}
            isUnallocatedTask={isUnallocatedTask}
          />
        ) : ""}

        {showProjectModal && (
          <ShowSingleProjectModal
            isVisible={showProjectModal}
            project_id={selectedPorject}
            taskData={selectedTask}
            closeModal={handleCloseProjectModal}
          />
        )}
      </div >
    </div>
  );
}

export default TimelineComponent;